import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const { t, language } = useLanguage();
  const { login, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load remembered email
  useEffect(() => {
    const saved = localStorage.getItem('remember_email');
    if (saved) setEmail(saved);
  }, []);

  // Auto-hide error after 2 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const validate = (): boolean => {
    if (!email.trim()) {
      setError(language === 'en' ? 'Email is required' : 'इमेल आवश्यक छ');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError(language === 'en' ? 'Enter a valid email' : 'मान्य इमेल प्रविष्ट गर्नुहोस्');
      return false;
    }
    if (!password.trim()) {
      setError(language === 'en' ? 'Password is required' : 'पासवर्ड आवश्यक छ');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      // Store or clear remembered email
      if (remember) {
        localStorage.setItem('remember_email', email);
      } else {
        localStorage.removeItem('remember_email');
      }

      await login(email, password);
    } catch (err: any) {
      // Show login failure (wrong email/password) for 2 seconds
      const message =
        err?.response?.data?.message ||
        (language === 'np' ? 'Invalid email or password' : 'गलत इमेल वा पासवर्ड');
      setError(message);
    }

    
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t('auth.login')}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="label" htmlFor="email">
                {language === 'en' ? 'Email address' : 'इमेल ठेगाना'}
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  className="input pl-10"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="label" htmlFor="password">
                {language === 'en' ? 'Password' : 'पासवर्ड'}
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="input pl-10 pr-10"
                  placeholder={language === 'en' ? 'Enter your password' : 'पासवर्ड प्रविष्ट गर्नुहोस्'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={language === 'en' ? 'Toggle password visibility' : 'पासवर्ड देखाउनु/लुकाउनुहोस्'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me + Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                {language === 'en' ? 'Remember me' : 'मलाई सम्झनुहोस्'}
              </label>
              <Link to="/auth/forgot-password" className="text-sm text-primary-600 hover:text-primary-800">
                {language === 'en' ? 'Forgot password?' : 'पासवर्ड बिर्सनुभयो?'}
              </Link>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex justify-center"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <span className="loading-spinner mr-2"></span>
                    {language === 'en' ? 'Signing in...' : 'साइन इन हुँदै...'}
                  </span>
                ) : (
                  language === 'en' ? 'Sign in' : 'साइन इन'
                )}
              </button>
            </div>

            <p className="text-center text-sm text-gray-600">
              {language === 'en' ? "Don't have an account?" : 'खाता छैन?'}{' '}
              <Link to="/auth/register" className="text-primary-600 hover:text-primary-800">
                {language === 'en' ? 'Register' : 'दर्ता गर्नुहोस्'}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
